import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useRouteMatch, Link } from 'react-router-dom';
import { UploadChangeParam } from 'antd/lib/upload/interface';
import Configs from 'config';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import scrollIntoView from 'scroll-into-view';
import BoardDetail from 'components/base/BoardDetail';
import SearchInput from 'components/base/SearchInput';
import { CSLabBoardWrapper } from 'components/cslab/CSLabBoard';
import {
  getUpcomingWebinars,
  getPastWebinars,
  getWebinar,
  createWebinar,
  updateWebinar,
  deleteWebinar,
} from 'libs/api/board';
import { usePermission, usePagination, useTimezone, useDataApi } from 'libs/hooks';
import { filteredFileNames } from 'libs/utils';
import { TBoardDetail } from 'modules/board/types';
import { TListResponse, TPagination, RouteMatch } from 'modules/types';
import {
  CWindowWidth,
  CDateTimeFormat,
  CDefaultDateTimeFormat,
  CUploadFileProps,
} from 'constants/base.const';
import { EOperation } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import {
  Upload,
  message,
  DatePicker,
  Tabs,
  Avatar,
  Card,
  Col,
  Row,
  Form,
  List,
  Input,
  Button,
  Typography,
} from 'antd';
import { EyeOutlined, UploadOutlined } from '@ant-design/icons/lib';
import { FormWrapper } from 'GlobalStyles';

const { Text, Title } = Typography;
const maxWidth = CWindowWidth - 300;
const { TabPane } = Tabs;

const CSWebinarContent = ({ getWebinars }: { getWebinars: any }) => {
  const [form] = Form.useForm();
  const [isFirstRendering, setIsFirstRendering] = useState(true);
  const { adminPermission } = usePermission();
  const [pagination, setPagination] = usePagination(12);
  const reloadPage = (page?: Partial<TPagination>) => {
    setPagination({
      ...pagination,
      ...page,
    });
    setIsFirstRendering(false);
  };
  const [getDateTime, timezone] = useTimezone();
  const getPromise = getWebinars.bind(null, {
    params: {
      current: pagination.current,
      pageSize: pagination.pageSize,
      q: pagination.q,
    },
  });
  const [{ data, loading }, setCallback] = useDataApi<TListResponse<TBoardDetail>>(getPromise, {
    contents: [],
    last: false,
  });

  useEffect(() => {
    setCallback(() => getPromise);
  }, [pagination]);

  useEffect(() => {
    if (!loading && !isFirstRendering) {
      scrollIntoView(document?.getElementById('card-list-header') as HTMLElement, {
        align: {
          top: 0.3,
        },
      });
    }
  }, [loading]);

  return (
    <div style={{ maxWidth: `${maxWidth}px`, margin: '20px auto', padding: '0px' }}>
      <Row justify='center' align='middle' gutter={[8, 8]} id='card-list-header'>
        <Col>
          <SearchInput pagination={pagination} reloadPage={reloadPage} />
        </Col>
        {adminPermission && (
          <Col>
            <Link to={`${ERoute.CSWebinar}/${EOperation.Create}`}>
              <Button className='add-button' type='primary' size='large'>
                글쓰기
              </Button>
            </Link>
          </Col>
        )}
      </Row>
      <List
        pagination={{
          ...pagination,
          total: get(data, 'total', 0),
          position: 'bottom',
          onChange: (page) => {
            reloadPage({
              current: page,
              pageSize: pagination.pageSize,
            });
          },
        }}
        className='card'
        loading={loading}
        style={{ paddingBottom: '20px', marginTop: '30px' }}
        grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 3 }}
        dataSource={get(data, 'contents')}
        renderItem={(item) => (
          <List.Item>
            <Link to={`${ERoute.CSWebinar}/${EOperation.View}/${get(item, 'id')}`}>
              <Card hoverable cover={<Avatar shape='square' src={get(item, 'thumbnail.absolute_url')} />}>
                <Card.Meta
                  title={
                    <div style={{ height: '50px' }} className='ellipsis-line'>
                      {get(item, 'title')}
                    </div>
                  }
                  description={
                    <Row justify='start'>
                      <Col style={{ marginBottom: '5px' }} span={24}>
                        {getDateTime(String(get(item, 'start_at')))
                          .format(CDateTimeFormat)
                          .replace('PM', '오후')
                          .replace('AM', '오전')}
                      </Col>
                      <Col span={24}>
                        <EyeOutlined />
                        &nbsp;{get(item, 'hit_count')}
                      </Col>
                    </Row>
                  }
                />
              </Card>
            </Link>
          </List.Item>
        )}
      />
    </div>
  );
};

const CSWebinar = () => {
  const [key, setKey] = useState('upcoming');

  return (
    <CSLabBoardWrapper>
      <Helmet>
        <title>CS 웨비나</title>
      </Helmet>
      <div className='title_wrapper'>
        <Title> CS 웨비나 </Title>
        <Text style={{ marginTop: '10px', fontSize: '16px' }}>
          Professional firm 업계 최신 정보와 채용 트렌드를 웨비나에서 확인하세요.
        </Text>
      </div>
      <Tabs
        activeKey={key}
        onChange={(e) => {
          setKey(e);
        }}
        centered={true}
      >
        <TabPane tab='다가오는 웨비나' key='upcoming'>
          <CSWebinarContent getWebinars={getUpcomingWebinars} />
        </TabPane>
        <TabPane tab='지난 웨비나' key='past'>
          <CSWebinarContent getWebinars={getPastWebinars} />
        </TabPane>
      </Tabs>
    </CSLabBoardWrapper>
  );
};

export const CSWebinarDetail = () => {
  const match = useRouteMatch(`${ERoute.CSWebinar}/:operation/:record_id?`);
  let { operation = EOperation.View, record_id } = (match?.params as RouteMatch) || {};
  const [{ data, loading }] = useDataApi<TBoardDetail>(
    getWebinar.bind(null, record_id),
    {},
    operation != EOperation.Create,
  );
  const [getDateTime, timezone] = useTimezone();
  const [form] = Form.useForm();

  useEffect(() => {
    if (isEmpty(data) === false) {
      form.setFieldsValue({
        start_at: moment(get(data, 'start_at'), CDefaultDateTimeFormat),
        link: get(data, 'link'),
        description: get(data, 'description'),
        images:
          get(data, 'profile_image.id', 0) > 0
            ? [
                {
                  uid: get(data, 'profile_image.id'),
                  status: 'done',
                  name: 'profile_image',
                },
              ]
            : [],
      });
    }
  }, [data]);

  const WebinarCard = () => {
    return (
      <>
        {operation === EOperation.View ? (
          <CSLabBoardWrapper style={{ paddingTop: '40px' }}>
            <Card
              className='card'
              cover={<Avatar shape='square' src={get(data, 'profile_image.absolute_url')} />}
            >
              <Row justify='start'>
                <Col span={24}>
                  {getDateTime(String(get(data, 'start_at')))
                    .format(CDateTimeFormat)
                    .replace('PM', '오후')
                    .replace('AM', '오전')}
                </Col>
              </Row>
              <Row justify='start'>
                <Col style={{ margin: '5px 0px' }} className='content-block' span={24}>
                  <div
                    style={{ color: '#000' }}
                    dangerouslySetInnerHTML={{
                      __html: `${get(data, 'description', '')}`,
                    }}
                  />
                </Col>
              </Row>
            </Card>
            {get(data, 'link') === 'inactive' ? (
              <Button
                style={{ width: '100%' }}
                onClick={() => {
                  message.error('수업 10분전부터 입장 가능합니다');
                }}
              >
                참여하기
              </Button>
            ) : (
              <a target='_blank' href={get(data, 'link')}>
                <Button style={{ width: '100%' }}>참여하기</Button>
              </a>
            )}
          </CSLabBoardWrapper>
        ) : (
          <FormWrapper style={{ paddingTop: '40px' }}>
            <Form form={form} layout='vertical' className='form-inline'>
              <Form.Item className='form-item' name='start_at'>
                <DatePicker showTime format={CDateTimeFormat} placeholder='웨비나 시점' />
              </Form.Item>
              <Form.Item
                className='form-item'
                name='link'
                extra='줌 링크에서 pwd 제거해야 입장할 때 비밀번호 검증 가능'
              >
                <Input placeholder='줌 링크(https://...)' />
              </Form.Item>
              <Form.Item name='description' className='form-item no-margin'>
                <Input.TextArea
                  className='form_textarea'
                  maxLength={400}
                  autoSize={{ minRows: 5 }}
                  placeholder='발표자 이력'
                />
              </Form.Item>
              <Form.Item
                noStyle
                name='images'
                shouldUpdate={(prevValues, currentValues) => prevValues.images !== currentValues.images}
                valuePropName='fileList'
                getValueFromEvent={(e: UploadChangeParam) => (Array.isArray(e) ? e : e && e?.fileList)}
              >
                <Upload.Dragger
                  name='file'
                  {...CUploadFileProps}
                  action={`${Configs.API_HOST}/board/image`}
                  onChange={(info: UploadChangeParam) => {
                    const { status } = info.file;
                    if (status === 'done') {
                      message.success(`${info.file.name} 업로드에 성공했습니다.`);
                    } else if (status === 'error') {
                      message.error(`${info.file.name} 업로드에 실패했습니다.`);
                    }
                  }}
                >
                  <UploadOutlined />
                  발표자 이미지 업로드
                </Upload.Dragger>
              </Form.Item>
            </Form>
          </FormWrapper>
        )}
      </>
    );
  };

  return (
    <Row
      style={{
        maxWidth: `${CWindowWidth - 200}px`,
        margin: '20px auto',
        padding: '30px 10px',
      }}
      gutter={[32, 16]}
    >
      <Col
        xs={{ span: 24, order: 2 }}
        sm={{ span: 24, order: 2 }}
        md={{ span: 17, order: 1 }}
        lg={{ span: 17, order: 1 }}
        xl={{ span: 17, order: 1 }}
      >
        <BoardDetail
          operation={operation}
          pathName={ERoute.CSWebinar}
          promiseCreate={createWebinar}
          promiseDelete={deleteWebinar}
          promiseUpdate={updateWebinar}
          getExtraData={() => {
            return {
              start_at: form.getFieldValue('start_at'),
              link: form.getFieldValue('link'),
              description: form.getFieldValue('description'),
              profile_image_ids: filteredFileNames(form.getFieldValue('images')),
            };
          }}
          activateComment={true}
          record={data}
          loading={loading}
        />
      </Col>
      <Col
        xs={{ span: 24, order: 1 }}
        sm={{ span: 24, order: 1 }}
        md={{ span: 7, order: 2 }}
        lg={{ span: 7, order: 2 }}
        xl={{ span: 7, order: 2 }}
      >
        <WebinarCard />
      </Col>
    </Row>
  );
};

export default CSWebinar;
